import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { BookOpen, Download, Copy, Check, Leaf, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function GbifPublication() {
  const { publicationId } = useParams();
  const [pub, setPub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/gbif/public/${publicationId}`);
        setPub(data.publication);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Publication not found');
      } finally { setLoading(false); }
    })();
  }, [publicationId]);

  const copyCitation = async () => {
    try {
      await navigator.clipboard.writeText(pub.citation_text);
      setCopied(true); setTimeout(() => setCopied(false), 2200);
      toast.success('Citation copied');
    } catch { toast.error('Copy failed'); }
  };

  const download = () => {
    window.open(`${api.defaults.baseURL}/gbif/public/${publicationId}/download`, '_blank');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#04120c] flex items-center justify-center text-white/60 text-sm">Loading…</div>
  );

  if (!pub) return (
    <div className="min-h-screen bg-[#04120c] text-foreground flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <p className="text-white/70">This publication is unavailable or has been retracted.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#04120c] text-foreground pb-24" data-testid="gbif-publication-root">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass border border-primary/30 mb-4"
        >
          <Leaf className="w-3.5 h-3.5 text-primary-light" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-primary-light">
            WildQuest Biodiversity Network
          </span>
        </motion.div>

        <h1 className="font-serif text-3xl leading-tight mb-2" data-testid="pub-title">{pub.title}</h1>
        {pub.description && (
          <p className="text-white/70 text-sm mb-5">{pub.description}</p>
        )}

        <div className="grid sm:grid-cols-3 gap-2 mb-5">
          <div className="p-3 rounded-2xl glass border border-white/10">
            <p className="text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> Observations
            </p>
            <p className="text-xl font-serif mt-1" data-testid="pub-obs-count">{pub.observation_count}</p>
          </div>
          <div className="p-3 rounded-2xl glass border border-white/10">
            <p className="text-[9px] uppercase tracking-widest text-white/50">Distinct Species</p>
            <p className="text-xl font-serif mt-1" data-testid="pub-species-count">{pub.distinct_species}</p>
          </div>
          <div className="p-3 rounded-2xl glass border border-white/10">
            <p className="text-[9px] uppercase tracking-widest text-white/50 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Published
            </p>
            <p className="text-sm mt-1">{new Date(pub.created_at).toLocaleDateString()}</p>
          </div>
        </div>

        {pub.filter_summary?.bbox && (
          <div className="p-3 rounded-2xl glass border border-white/10 mb-4 flex items-center gap-2 text-xs">
            <MapPin className="w-3.5 h-3.5 text-primary-light" />
            <span className="text-white/70">
              Bounding box: SW ({pub.filter_summary.bbox[0].toFixed(2)}, {pub.filter_summary.bbox[1].toFixed(2)}) →
              NE ({pub.filter_summary.bbox[2].toFixed(2)}, {pub.filter_summary.bbox[3].toFixed(2)})
            </span>
          </div>
        )}

        {/* Citation panel */}
        <section className="p-4 rounded-3xl glass border border-white/10 mb-4" data-testid="citation-panel">
          <p className="text-[10px] uppercase tracking-widest text-primary-light mb-2">Citation</p>
          <p className="text-sm text-white/90 leading-relaxed font-mono">{pub.citation_text}</p>
          <div className="flex items-center gap-2 mt-3">
            <button
              data-testid="btn-copy-citation"
              onClick={copyCitation}
              className="px-3 py-1.5 rounded-full bg-primary text-white text-xs flex items-center gap-1"
            >
              {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy citation</>}
            </button>
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              Identifier: <span className="text-white/70 font-mono">{pub.doi_stub}</span>
            </span>
          </div>
        </section>

        {/* Download */}
        <section className="p-4 rounded-3xl glass border border-white/10">
          <p className="text-[10px] uppercase tracking-widest text-primary-light mb-2">Data Package</p>
          <p className="text-xs text-white/70 mb-3">
            Darwin Core Archive (ZIP) containing <code>occurrence.txt</code>, <code>meta.xml</code>, and <code>eml.xml</code> —
            GBIF-compatible. Drop it straight into the GBIF IPT or import into your analysis pipeline.
          </p>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-white/50">
              <span>Size:</span> <span className="text-white/70">{Math.round((pub.dwca_size_bytes || 0) / 1024)} KB</span>
              <span className="mx-2">·</span>
              <span>License:</span> <span className="text-white/70">{pub.license || 'CC-BY-4.0'}</span>
            </div>
            <button
              data-testid="btn-download-dwca"
              onClick={download}
              className="px-4 py-2 rounded-full bg-primary text-white text-xs flex items-center gap-1"
            >
              <Download className="w-3.5 h-3.5" /> Download DwC-A
            </button>
          </div>
        </section>

        <p className="text-[10px] uppercase tracking-widest text-white/40 text-center mt-8">
          Published from WildQuest · Verified public observations only
        </p>
      </div>
    </div>
  );
}
